import { ApolloError } from "@apollo/client";
import { ErrorReport } from "./Datadog";
import { extractErrorReportFromApolloError } from "./Apollo";
import isNotNullish from "./isNotNullish";

export class DetailedError extends Error implements ErrorReport {
  context = this.errorReport?.context;
  errors = this.errorReport?.errors ?? [];
  constructor(
    public readonly code: string,
    readonly body: string,
    private readonly errorReport?: ErrorReport
  ) {
    super(`[${code}] ${body}`);
  }
}

// ==================================================
// ASSORTMENT [ASMT]
// ==================================================
const AssortmentErrorCodes = {
  FinancialOverviewTableQueryFetchFailure: "ASMT1004",
  FinancialsPriceChangeQueryFetchFailure: "ASMT1005",
  ProductListQueryFetchFailure: "ASMT1006",
  WorkspaceDetailsQueryFetchFailure: "ASMT1008",
  WorkspaceDetailsQueryDeletedFailure: "ASMT1009",
  WorkspaceDetailsQueryPermissionsFailure: "ASMT1010",
} as const;

/**
 * This error code is thrown when we are unable to get assortment data in
 * the `useProductListQuery` hook.
 */
export class ProductListQueryFetchFailure extends DetailedError {
  constructor(errors?: Record<string, ApolloError | undefined>) {
    super(
      AssortmentErrorCodes.ProductListQueryFetchFailure,
      "Failed to load assortment information.",
      {
        message: "Multiple errors.",
        context: errors,
        errors: errors
          ? Object.values(errors)
              .map((error) => error && extractErrorReportFromApolloError(error))
              .filter(isNotNullish)
          : [],
      }
    );
  }
}

/**
 * This error code is thrown when we are unable to get assortment data in
 * the `useWorkspaceContext` function hook.
//  */
export class WorkspaceDetailsQueryFetchFailure extends DetailedError {
  constructor(details?: ApolloError) {
    super(
      AssortmentErrorCodes.WorkspaceDetailsQueryFetchFailure,
      "Failed to load assortment information.",
      details && extractErrorReportFromApolloError(details)
    );
  }
}

/**
 * This error code is thrown when we are unable to get assortment data in
 * the `useWorkspaceContext` function hook.
//  */
export class WorkspaceDetailsQueryDeletedFailure extends DetailedError {
  constructor(details?: ApolloError) {
    super(
      AssortmentErrorCodes.WorkspaceDetailsQueryDeletedFailure,
      "This order has been deleted.",
      details && extractErrorReportFromApolloError(details)
    );
  }
}

/**
 * This error code is thrown when we are unable to get assortment data in
 * the `useWorkspaceContext` function hook.
//  */
export class WorkspaceDetailsQueryPermissionsFailure extends DetailedError {
  constructor(details?: ApolloError) {
    super(
      AssortmentErrorCodes.WorkspaceDetailsQueryPermissionsFailure,
      "You do not have permission to view this order.",
      details && extractErrorReportFromApolloError(details)
    );
  }
}

export class FinancialOverviewTableQueryFetchFailure extends DetailedError {
  constructor(details?: ApolloError) {
    super(
      AssortmentErrorCodes.FinancialOverviewTableQueryFetchFailure,
      "Failed to load assortment information.",
      details && extractErrorReportFromApolloError(details)
    );
  }
}

/**
 * This error code is thrown when we are unable to get assortment data in
 * the `<FinancialsPriceChangeContainer />` component.
 */
export class FinancialsPriceChangeQueryFetchFailure extends DetailedError {
  constructor(details?: ApolloError) {
    super(
      AssortmentErrorCodes.FinancialsPriceChangeQueryFetchFailure,
      "Failed to load assortment information.",
      details && extractErrorReportFromApolloError(details)
    );
  }
}

// ==================================================
// AUTH [AU]
// ==================================================

/**
 * This error code is thrown when we are unable to get an auth principal.
 * Without an auth principal we are not able to initialize many features
 * including launch darkly and we are not able to display logged in user info.
 *
 * Furthermore, due to how fetching the auth principal is implemented, it is
 * likely that if we are unable to get an auth principal the user is not logged
 * in or either atlas or backend are down.
 */
export class AuthPrincipalFetchFailure extends DetailedError {
  constructor(details?: ApolloError) {
    super(
      "AU1001",
      "Failed to load account information.",
      details && extractErrorReportFromApolloError(details)
    );
  }
}
